import { logger } from "@/lib/logger";
/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                       BROWSER-SAFE UTILITIES                              ║
 * ║                                                                           ║
 * ║  ⚠️  CENTRALIZED MODULE - DO NOT DUPLICATE THESE FUNCTIONS!               ║
 * ║                                                                           ║
 * ║  Import: import { safeIsOnline, ... } from '@/lib/browserSafeUtils'       ║
 * ║  Or:     import { safeIsOnline, ... } from '@/lib'                        ║
 * ║                                                                           ║
 * ║  ESLint will WARN if you try to create local copies of these functions.   ║
 * ║                                                                           ║
 * ║  See: src/lib/README.md for full documentation                            ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 * 
 * These utilities safely access browser APIs, handling:
 * - SSR/pre-rendering contexts (typeof checks)
 * - Private browsing mode (try-catch for storage)
 * - Browser security restrictions
 * - IndexedDB availability
 * 
 * @module browserSafeUtils
 */

/**
 * Safely check if navigator.onLine is true
 * Returns true if we can't check (fail-open for online operations)
 */
export function safeIsOnline(): boolean {
  try {
    if (typeof navigator === 'undefined') {
      return true; // Assume online in non-browser contexts
    }
    return navigator.onLine;
  } catch {
    return true; // Assume online if we can't check
  }
}

/**
 * Safely get a value from localStorage
 * Returns null if localStorage is unavailable or key doesn't exist
 */
export function safeGetStorage(key: string): string | null {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    return localStorage.getItem(key);
  } catch (error) {
    logger.warn('[browserSafeUtils] localStorage read error:', error);
    return null;
  }
}

/**
 * Safely set a value in localStorage
 * Returns true if successful, false otherwise
 * Handles QuotaExceededError by attempting cleanup of stale items
 */
export function safeSetStorage(key: string, value: string): boolean {
  try {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    localStorage.setItem(key, value);
    return true;
  } catch (error: any) {
    // QuotaExceededError — attempt to free space
    if (error?.name === 'QuotaExceededError' || error?.code === 22 || error?.code === 1014) {
      logger.warn('[browserSafeUtils] localStorage quota exceeded, attempting cleanup');
      try {
        // Remove known expendable keys (oldest cached data)
        const expendablePatterns = ['offline-request-queue', 'offline-edge-functions', 'offline-failed-queue'];
        for (const pattern of expendablePatterns) {
          const existing = localStorage.getItem(pattern);
          if (existing && existing.length > 1000) {
            // Trim to most recent items only
            try {
              const parsed = JSON.parse(existing);
              if (Array.isArray(parsed) && parsed.length > 10) {
                localStorage.setItem(pattern, JSON.stringify(parsed.slice(-10)));
              }
            } catch {
              // Can't parse, leave it
            }
          }
        }
        // Retry the original write
        localStorage.setItem(key, value);
        return true;
      } catch {
        logger.error('[browserSafeUtils] localStorage quota exceeded even after cleanup — data may be lost');
        return false;
      }
    }
    logger.warn('[browserSafeUtils] localStorage write error:', error);
    return false;
  }
}

/**
 * Safely remove a value from localStorage
 */
export function safeRemoveStorage(key: string): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key);
    }
  } catch {
    // Ignore errors
  }
}

/**
 * Safely dispatch a custom event on window
 */
export function safeDispatchEvent(name: string, detail?: any): void {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(name, { detail }));
    }
  } catch (error) {
    logger.warn('[browserSafeUtils] Failed to dispatch event:', error);
  }
}

/**
 * Check if IndexedDB is available in this environment
 */
export function isIndexedDBAvailable(): boolean {
  try {
    if (typeof indexedDB === 'undefined' || !indexedDB) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely get network connection info (for connection quality)
 */
export function safeGetConnection(): any {
  try {
    if (typeof navigator === 'undefined') {
      return null;
    }
    return (navigator as any).connection 
      || (navigator as any).mozConnection 
      || (navigator as any).webkitConnection
      || null;
  } catch {
    return null;
  }
}

/**
 * Check if window object is available
 */
export function hasWindow(): boolean {
  return typeof window !== 'undefined';
}
